import { css } from "@emotion/css";
import { ErrorPanel } from "@octopusdeploy/design-system-components";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentV2Resource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { useOptionalProjectContext } from "~/areas/projects/context/index";
import { ArgoCDApplicationsPreview } from "~/components/Actions/argocd/ApplicationsPreview/ArgoCDApplicationsPreview";
import { CreateArgoCDGateway } from "~/components/Actions/argocd/ApplicationsPreview/CreateArgoCDGateway";
import { NoEnvironmentsSelected } from "~/components/Actions/argocd/ApplicationsPreview/NoEnvironmentsSelected";
import type { DeploymentConfiguration, ArgoCdInstancesAndApplications } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { deploymentEnvironments, isScopedToDeploymentConfiguration, useArgoCDContext } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import { NoMappedApplications } from "./NoMappedApplications";
const styles = {
    skeleton: css({
        height: "24px",
    }),
};
type ContentPanelProps = {
    argoCDContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
    project: ProjectResource;
    deploymentConfiguration?: DeploymentConfiguration;
    showSelectedEnvironmentsOnly: boolean;
    environments: EnvironmentV2Resource[];
};
const ContentPanel = (props: ContentPanelProps) => {
    const { argoCDContext, project, deploymentConfiguration, showSelectedEnvironmentsOnly, environments } = props;
    if ((argoCDContext.isLoading && !argoCDContext.result) || (showSelectedEnvironmentsOnly && !deploymentConfiguration)) {
        return (<div className={styles.skeleton}>
                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
            </div>);
    }
    if (argoCDContext.error) {
        const errorInfo = createErrorInfoFromError(argoCDContext.error);
        return <ErrorPanel error={errorInfo}/>;
    }
    if (argoCDContext.result) {
        if (argoCDContext.result.instances.Resources.length === 0) {
            return <CreateArgoCDGateway refetch={props.argoCDContext.refetch} spaceId={props.project.SpaceId}/>;
        }
        const projectMappedApps = argoCDContext.result.applications.filter((application) => isScopedToDeploymentConfiguration(application, project.Id, deploymentConfiguration));
        if (showSelectedEnvironmentsOnly && deploymentEnvironments(deploymentConfiguration).length === 0) {
            return <NoEnvironmentsSelected />;
        }
        else if (projectMappedApps.length === 0) {
            return <NoMappedApplications refetch={argoCDContext.refetch} projectSlug={project.Slug}/>;
        }
        else {
            return (<ArgoCDApplicationsPreview refetchArgoContext={argoCDContext.refetch} applications={projectMappedApps} project={project} deploymentConfiguration={deploymentConfiguration} environments={environments} gateways={argoCDContext.result.instances.Resources}/>);
        }
    }
    return <ErrorPanel error={{ message: "Failed to handle Argo instances and applications, deployment preview is unavailable.", details: [] }}/>;
};
type ArgoCDApplicationsPreviewFormSectionProps = {
    expandedByDefault?: boolean;
    deploymentConfiguration?: DeploymentConfiguration;
    showSelectedEnvironmentsOnly?: boolean;
    environments: EnvironmentV2Resource[];
};
export const ArgoCDApplicationsPreviewFormSection = (props: ArgoCDApplicationsPreviewFormSectionProps) => {
    const { expandedByDefault, deploymentConfiguration, showSelectedEnvironmentsOnly, environments } = props;
    const projectContext = useOptionalProjectContext();
    const argoCDContext = useArgoCDContext(projectContext?.state.model.Id);
    if (!projectContext) {
        return <></>;
    }
    return (<ExpandableFormSection errorKey="Octopus.Action.ArgoCD.ApplicationsPreview" title="Argo CD Applications" help="Preview which Argo CD applications this step will update with your current configuration." summary={Summary.summary("Preview which Argo CD applications this step will update with your current configuration.")} isExpandedByDefault={expandedByDefault}>
            <ContentPanel project={projectContext.state.model} deploymentConfiguration={deploymentConfiguration} argoCDContext={argoCDContext} showSelectedEnvironmentsOnly={showSelectedEnvironmentsOnly || false} environments={environments}/>
        </ExpandableFormSection>);
};
